import Link from "next/link";

import { RoundedUserIcon } from "@/assets/icons/general/RoundedUserIcon";
import { JobSalaryLastPrice } from "@/components/job-salary-ticker/JobSalaryLastPrice";
import { JobSalaryNetChange } from "@/components/job-salary-ticker/JobSalaryNetChange";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { useJobData } from "@/hooks/useJobData";
import { useJobTitleUserCounts } from "@/hooks/useJobTitleUserCounts";

interface ShortlistBoxItemProps {
  jobId: string;
}

export const ShortlistBoxItem = ({ jobId }: ShortlistBoxItemProps) => {
  const { jobData, isLoading, error } = useJobData(jobId);
  const { jobTitleUserCountsMap } = useJobTitleUserCounts({
    jobTitles: [jobData?.title || ""],
    selectedMetroArea: jobData?.metroArea || null,
    selectedQuantile: jobData?.quantile || null,
    enabled: !isLoading && !!jobData,
  });

  const profileCount = jobTitleUserCountsMap.get(jobData?.title || "");

  if (isLoading) {
    return <Loader className="h-10 w-10" />;
  }

  if (error || !jobData) {
    return (
      <div className="flex max-w-[222px] cursor-pointer flex-col gap-2 rounded-xs border border-info-error-outline px-3 py-4">
        <Typography
          size="label-2"
          variant="prominent-2"
          className="truncate text-info-error-outline"
        >
          {error || "Job not found"}
        </Typography>
      </div>
    );
  }

  return (
    <Link
      href={`/job/${jobId}`}
      className="flex w-full max-w-[222px] flex-1"
    >
      <div className="flex w-full flex-1 cursor-pointer flex-col gap-2 rounded-xs border border-elevation-outline-2 px-3 py-4">
        <Typography
          size="label-4"
          variant="prominent-1"
          className="truncate text-text-default-primary"
        >
          {jobData.title}
        </Typography>
        <div className="flex flex-row gap-2">
          <div className="flex w-3/5 flex-row items-center gap-0-5 rounded-[22px] border border-elevation-outline-1 px-2-5 py-0-5">
            <Tooltip text={jobData.metroArea.title}>
              <CustomTooltipTrigger className="relative h-full w-full">
                <Typography
                  size="label-5"
                  variant="prominent-1"
                  className="truncate text-text-default-secondary"
                >
                  {jobData.metroArea.title}
                </Typography>
              </CustomTooltipTrigger>
            </Tooltip>
          </div>
          <div className="flex w-2/5 flex-row items-center justify-center gap-0-5 rounded-[22px] border border-elevation-outline-1 px-2-5 py-0-5">
            <Typography
              size="label-5"
              variant="prominent-1"
              className="truncate text-text-default-secondary"
            >
              {jobData.quantile.title}
            </Typography>
          </div>
        </div>
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row gap-2">
            <Typography
              size="body-3"
              variant="prominent-2"
              className="text-text-inverted-tertiary"
            >
              <JobSalaryLastPrice jobSalary={jobData} />
            </Typography>
            <Typography
              size="body-3"
              variant="prominent-2"
              className="text-text-inverted-tertiary"
            >
              <JobSalaryNetChange jobSalary={jobData} />
            </Typography>
          </div>
          <div className="flex flex-row items-center justify-center gap-1 text-text-inverted-secondary">
            <RoundedUserIcon className="h-4 w-4" />
            <Typography size="label-4">{profileCount}</Typography>
          </div>
        </div>
      </div>
    </Link>
  );
};
